<template>
    <div id="manage-subjects" class="relative h-full flex flex-col h-full p-4">
        <div>
            <div class="flex justify-between items-center h-10">
                <h1 class="m-0 truncate">{{ $t('manage_school_subjects_header') }}</h1>
                <XButton @click="add()" :disabled="disableAll || loading" class="shadow-text-none" bg-color="#28a745">
                    <i class="fas fa-plus"></i>
                    <span class="hidden xl:inline">{{ $t('manage_school_subjects_button_add') }}</span>
                </XButton>
            </div>
            <!-- <ManageSearch
                @search="onSearch($event)"
                :disabled-search="disableAll || loading"
                ref="manageSearch"
                class="mt-4"
            /> -->
        </div>
        <div id="subjects-section" class="relative flex-1 overflow-hidden">
            <ManageTable :list="subjects" :loading="loading" :paginator="false">
                <template #header>
                    <h3 class="w-3/5 m-0 font-size-16">{{ $t('manage_school_subjects_table_header_name') }}</h3>
                    <h3 class="w-1/5 m-0 font-size-16">{{ $t('manage_school_subjects_table_header_status') }}</h3>
                    <h3 class="w-1/5 m-0 font-size-16 text-right">
                        {{ $t('manage_school_subjects_table_header_actions') }}
                    </h3>
                </template>
                <template v-slot="{ item }">
                    <div class="flex flex-col md:flex-row w-full">
                        <div class="flex items-center w-full md:w-4/5">
                            <div class="flex-1 md:w-3/4 truncate">
                                {{ item.label }}
                            </div>
                            <div class="md:w-1/4 flex items-center">
                                <XToggleSwitch
                                    @input="toggleStatus($event, item)"
                                    :checked="item.status"
                                    :show-background="true"
                                    :show-toggle-symbol="false"
                                    color="rgba(40, 167, 69, .3)"
                                    border-color="#28a745"
                                    checked-color="#28a745"
                                    height="22"
                                    class="inline-block"
                                />
                            </div>
                        </div>
                        <div class="settings flex justify-end md:w-1/5">
                            <div class="flex md:justify-end w-full xl:w-auto">
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="edit(item)"
                                    :content="$t('manage_school_subject_table_tippy_edit_subject')"
                                    :disabled="item.editLoading || disableAll"
                                    bg-color="#17a2b8"
                                >
                                    <span v-if="item.editLoading" class="close-loader"></span>
                                    <i v-else class="fas fa-edit"></i>
                                </XButton>
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="remove(item)"
                                    :content="$t('manage_school_subject_table_tippy_remove_subject')"
                                    :disabled="item.removeLoading || disableAll"
                                    bg-color="#dc3545"
                                >
                                    <span v-if="item.removeLoading" class="close-loader"></span>
                                    <i v-else class="fas fa-trash-alt"></i>
                                </XButton>
                            </div>
                        </div>
                    </div>
                </template>
                <template #empty>
                    <div class="flex justify-center items-center w-full h-full">
                        <div class="flex flex-col justify-center items-center max-w-xl">
                            <i class="fas fa-book-reader"></i>
                            <h4 v-html="$t('manage_school_subjects_empty_state')" class="text-center m-0 mt-4"></h4>
                        </div>
                    </div>
                </template>
                <template #loading>
                    <InnerLoader color="#333" class="pin-t pin-l" />
                </template>
            </ManageTable>
        </div>
        <ManageSubjectDetail
            v-if="showManageSubjectDetail"
            @close="showManageSubjectDetail = false"
            @success="
                showManageSubjectDetail = false
                fetchSubjects()
            "
            :subject-list="subjects"
            :subject="subjectDetail"
            class="absolute pin-t pin-l w-full z-10"
        />
    </div>
</template>

<script>
import popup from '@/lib/popup.js'
import XButton from '@/components/v2/XButton'
import ManageTable from '@/components/manage-school/ManageTable'
import ManageSubjectDetail from '@/components/manage-school/ManageSubjectDetail'
import InnerLoader from '@/components/InnerLoader'
import XToggleSwitch from '@/components/v2/XToggleSwitch'

export default {
    components: {
        XButton,
        ManageTable,
        ManageSubjectDetail,
        InnerLoader,
        XToggleSwitch
    },

    data() {
        return {
            subjects: null,
            loading: false,
            disableAll: false,
            showManageSubjectDetail: false
        }
    },

    created() {
        this.fetchSubjects()
    },

    methods: {
        async fetchSubjects() {
            this.loading = true

            this.subjects = null

            try {
                const res = await this.$store.dispatch('getSubjects')

                this.subjects = res.subjects.map(subject => {
                    return {
                        ...subject,
                        editLoading: false,
                        removeLoading: false
                    }
                })
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
            }
        },

        async toggleStatus(event, subject) {
            if (this.disableAll) return

            this.disableAll = true

            const isChecked = event.target.checked

            try {
                this.subjects.find(data => data.name === subject.name).status = isChecked

                await this.$store.dispatch('updateSubjects', {
                    subjects: this.subjects
                })

                this.toastSuccess(this.$t('manage_school_subjects_popup_change_status_complete'))
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.disableAll = false
            }
        },

        add() {
            this.subjectDetail = null
            this.showManageSubjectDetail = true
        },

        async edit(subject) {
            subject.editLoading = true

            this.subjectDetail = subject
            this.showManageSubjectDetail = true

            await this.$nextTick()

            subject.editLoading = false
        },

        async remove(subject) {
            if (this.disableAll || subject.removeLoading) return

            popup.confirm(this.$t('manage_school_subjects_confirm_remove_subject'), async () => {
                this.disableAll = true
                subject.removeLoading = true

                try {
                    const payload = {
                        subjects: this.subjects.filter(data => data.name !== subject.name)
                    }

                    await this.$store.dispatch('updateSubjects', payload)

                    this.toastSuccess(this.$t('manage_school_subjects_popup_remove_subject_complete'))

                    this.fetchSubjects()
                } catch {
                    this.toastError(this.$t('manage_school_popup_error'))
                    subject.removeLoading = false
                } finally {
                    this.disableAll = false
                }
            })
        }
    }
}
</script>

<style lang="scss">
#manage-subjects {
    row-gap: 1rem;

    #subjects-section {
        .manage-table-item {
            // row-gap: 1rem;

            > div {
                row-gap: 1rem;

                .toggle-switch-box {
                    display: inline-block;
                }

                .settings {
                    > div {
                        column-gap: 0.5rem;

                        button {
                            width: 100%;
                            height: 2rem;

                            @media screen and (min-width: 768px) {
                                width: 2rem;
                                height: auto;
                                padding: 0;
                                aspect-ratio: 1 / 1;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
